/**
 * Who a `linkedin_article` post is published by: the publication's LinkedIn
 * newsletter publish target, never a social account.
 *
 * A `linkedin_article` row is the post LinkedIn attaches to a long-form
 * article. The browser service publishes it with the article, using that
 * target's captured session; Zernio never sends it. Its `account_id` exists
 * only because the rule step must name one, so showing that account's name
 * says something untrue: the founder's newsletter posts read as coming from
 * TeamsPerformance, a company page that has nothing to do with them
 * (2026-10-11). The publish target's name is the honest label.
 *
 * Pure, so the unit tests can load it; the hook is in `linkedin-article-label.ts`.
 */
export interface PublishTargetLookupRow {
  id: number;
  type: string;
  name: string;
  enabled: boolean;
  publication_id?: number | null;
  publicationId?: number | null;
}

/**
 * The target a publication's LinkedIn article goes out through: its
 * `linkedin_newsletter` target, preferring an enabled one, lowest id first.
 */
export function linkedinArticleTarget(
  targets: readonly PublishTargetLookupRow[],
  publicationId: number | null | undefined
): PublishTargetLookupRow | undefined {
  if (publicationId == null) return undefined;
  const candidates = targets
    .filter((t) => t.type === "linkedin_newsletter")
    .filter((t) => (t.publication_id ?? t.publicationId) === publicationId)
    .sort((a, b) => Number(b.enabled) - Number(a.enabled) || a.id - b.id);
  return candidates[0];
}

/** Falls back to the platform's own name, never to an account. */
export function linkedinArticleLabel(target: PublishTargetLookupRow | undefined): string {
  return target?.name ?? "LinkedIn article";
}
